<template>
  <div class="container v-content-container tooltip-wrapper popup-wrapper">
    <div class="row tooltip-wrapper">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-user fa-fw fa-users"></i>
          <span v-if="selectedContactPersonId != null">
            {{
              noDataExist
                ? "Customer"
                : beautifyName(originalFirstName, originalLastName)
            }}
          </span>
          <span v-else-if="!loading">
            {{ $label("customer.new_contact_person", "New contact person") }}
          </span>
        </h1>
      </div>
    </div>

    <navigation-tabs :customer-id="selectedContactPersonId"></navigation-tabs>

    <div class="row"><div class="col-xs-12">&nbsp;</div></div>

    <error404 v-if="noDataExist" />

    <template v-else>
      <div v-show="loading" class="row">
        <div class="col-xs-12">
          <view-loader
            margin-top-pixels="15"
            border-color="primary"
          ></view-loader>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12">
          <h2>{{ $label("customer.contact_person", "Contact person") }}</h2>
          <p></p>
          <div class="v-form-partial">
            <div class="v-form">
              <fieldset>
                <div class="row">
                  <!-- BEGIN Gender -->
                  <section class="col col-6">
                    <label class="label">{{
                      $label("common.gender", "Gender")
                    }}</label>
                    <label class="select">
                      <select
                        v-model="selectedContactPerson.gender"
                        :disabled="updating"
                        id="contact-person-gender"
                      >
                        <option value="0">{{
                          $label("common.gender", "Gender")
                        }}</option>
                        <option value="2">{{
                          $label("common.gender_male", "Male")
                        }}</option>
                        <option value="1">{{
                          $label("common.gender", "Female")
                        }}</option>
                      </select>
                      <i></i>
                    </label>
                  </section>
                  <!-- End Gender -->

                  <!-- BEGIN Title -->
                  <section class="col col-6">
                    <text-input
                      v-model="selectedContactPerson.title"
                      id="contact-person-title"
                      :label="$label('common.title', 'Title')"
                      :placeholder="$label('common.title', 'Title')"
                      :has-error="hasError('title')"
                      :error="getError('title')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateContactPerson"
                    />
                  </section>
                  <!-- END Title -->
                </div>
                <div class="row">
                  <!-- BEGIN Last name -->
                  <section class="col col-6">
                    <text-input
                      v-model="selectedContactPerson.last_name"
                      id="contact-person-last-name"
                      :label="$label('common.last_name', 'Last name')"
                      :placeholder="$label('common.last_name', 'Last name')"
                      :has-error="hasError('last_name')"
                      :error="getError('last_name')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateContactPerson"
                    />
                  </section>
                  <!-- END Last name -->

                  <!-- BEGIN First name -->
                  <section class="col col-6">
                    <text-input
                      v-model="selectedContactPerson.first_name"
                      id="contact-person-first-name"
                      :label="$label('common.first_name', 'First name')"
                      :placeholder="$label('common.first_name', 'First name')"
                      :has-error="hasError('first_name')"
                      :error="getError('first_name')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateContactPerson"
                    />
                  </section>
                  <!-- END First name -->
                </div>

                <div class="row">
                  <!-- BEGIN Email -->
                  <section class="col col-8">
                    <label class="label">{{
                      $label("common.email_address_long", "E-Mail address")
                    }}</label>
                    <label class="input">
                      <i class="icon-append icon-envelope"></i>
                      <input
                        v-model="selectedContactPerson.email"
                        :class="{ 'text-input-error': hasError('email') }"
                        :placeholder="$label('common.email_address_short')"
                        :disabled="updating"
                        id="contact-person-email"
                        type="email"
                      />
                    </label>

                    <p v-if="hasError('email')" class="text-danger">
                      {{ getError("email") }}
                    </p>
                  </section>
                  <!-- END Email -->

                  <!-- BEGIN Department -->
                  <section class="col col-4">
                    <text-input
                      v-model="selectedContactPerson.department"
                      id="contact-person-department"
                      :label="
                        $label('customer.contact_department', 'Department')
                      "
                      :placeholder="
                        $label('customer.contact_department', 'Department')
                      "
                      :has-error="hasError('department')"
                      :error="getError('department')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateContactPerson"
                    />
                  </section>
                  <!-- END Department -->
                </div>

                <div class="row">
                  <!-- BEGIN Mobile phone country code -->
                  <section class="col col-3">
                    <label class="label">{{
                      $label("common.prefix", "Prefix")
                    }}</label>
                    <label class="input">
                      <input
                        :class="{
                          'text-input-error': hasError(
                            'phone_number.mobile_prefix'
                          )
                        }"
                        type="text"
                        placeholder="XX"
                        :disabled="updating"
                        id="contact-person-mobile-phone-prefix"
                      />
                    </label>

                    <p
                      v-if="hasError('phone_number.mobile_prefix')"
                      class="text-danger"
                    >
                      {{ getError("phone_number.mobile_prefix") }}
                    </p>
                  </section>
                  <!-- END Mobile phone country code -->

                  <!-- BEGIN Mobile phone number -->
                  <section class="col col-9">
                    <label class="label">{{
                      $label(
                        "common.mobile_phone_number",
                        "Mobile phone number"
                      )
                    }}</label>
                    <label class="input">
                      <i class="icon-append fa fa-mobile-phone"></i>
                      <input
                        :class="{
                          'text-input-error': hasError(
                            'phone_number.mobile_number'
                          )
                        }"
                        :placeholder="
                          $label('common.phone_number_placeholder', 'XXXXXXXX')
                        "
                        :disabled="updating"
                        id="contact-person-mobile-phone-number"
                        type="tel"
                      />
                    </label>

                    <p
                      v-if="hasError('phone_number.mobile_number')"
                      class="text-danger"
                    >
                      {{ getError("phone_number.mobile_number") }}
                    </p>
                  </section>
                  <!-- END Mobile phone number -->
                </div>

                <div class="row">
                  <!-- BEGIN Phone country code -->
                  <section class="col col-3">
                    <label class="label">{{
                      $label("common.prefix", "Prefix")
                    }}</label>
                    <label class="input">
                      <input
                        v-model="selectedContactPerson.phone_number.prefix"
                        :class="{
                          'text-input-error': hasError('phone_number.prefix')
                        }"
                        type="text"
                        placeholder="XX"
                        :disabled="updating"
                        id="contact-person-phone-prefix"
                      />
                    </label>

                    <p
                      v-if="hasError('phone_number.prefix')"
                      class="text-danger"
                    >
                      {{ getError("phone_number.prefix") }}
                    </p>
                  </section>
                  <!-- END Phone country code -->

                  <!-- BEGIN Phone number -->
                  <section class="col col-9">
                    <label class="label">{{
                      $label("common.phone_number", "Phone number")
                    }}</label>
                    <label class="input">
                      <i class="icon-append fa fa-phone"></i>
                      <input
                        v-model="selectedContactPerson.phone_number.number"
                        :class="{
                          'text-input-error': hasError('phone_number.number')
                        }"
                        :placeholder="
                          $label('common.phone_number_placeholder', 'XXXXXXXX')
                        "
                        :disabled="updating"
                        id="contact-person-phone-number"
                        type="tel"
                      />
                    </label>

                    <p
                      v-if="hasError('phone_number.number')"
                      class="text-danger"
                    >
                      {{ getError("phone_number.number") }}
                    </p>
                  </section>
                  <!-- END Phone number -->
                </div>
              </fieldset>
            </div>
          </div>
        </div>
      </div>

      <div class="row"><div class="col-xs-12">&nbsp;</div></div>
      <hr />

      <div class="row">
        <div class="col-xs-3 text-center">&nbsp;</div>

        <div class="col-xs-6 text-center">
          <back-button
            back-link="CustomerContacts"
            :class="{ 'is-disabled': updating }"
            id="back-contact-person-button"
            icon="fa fa-lg fa-fw fa-arrow-circle-o-left"
            :text="$label('common.back', 'Back')"
          >
          </back-button>
          &nbsp;
          <info-button
            :class="{ 'is-disabled': updating }"
            id="save-contact-person-button"
            icon="fa fa-lg fa-fw fa-save"
            :text="$label('common.save', 'Save')"
            @click.native="createOrUpdateContactPerson"
          >
          </info-button>
          &nbsp;
          <danger-button
            :class="{
              'is-disabled': updating || selectedContactPersonId == null
            }"
            id="delete-contact-person-button"
            icon="fa fa-fw fa-lg fa-trash-o"
            :text="$label('common.delete', 'Delete')"
            @click.native="deleteContactPerson"
          >
          </danger-button>
        </div>

        <div class="col-xs-3 text-center">&nbsp;</div>
      </div>

      <div class="row"><div class="col-xs-12">&nbsp;</div></div>

      <div class="row mb-4">
        <div class="col-xs-12"></div>
      </div>
    </template>
  </div>
</template>

<script>
import swal from "sweetalert";
import Api from "@/api";

import FormErrorsMixin from "@/mixins/FormErrors";
import ViewLoader from "@/components/Common/ViewLoader";
import TextInput from "@/components/Common/Inputs/TextInput";
import InfoButton from "@/components/Common/Buttons/InfoButton";
import BackButton from "@/components/Common/Buttons/BackButton";
import DangerButton from "@/components/Common/Buttons/DangerButton";
import NavigationTabs from "@/components/Customers/Customer/NavigationTabs";
import Error404 from "@/components/Common/Error404";

export default {
  name: "CustomerContactDetails",

  mixins: [FormErrorsMixin],

  components: {
    ViewLoader,
    TextInput,
    InfoButton,
    BackButton,
    DangerButton,
    NavigationTabs,
    Error404
  },

  data() {
    return {
      loading: false,
      updating: false,
      noDataExist: false,
      originalFirstName: null,
      originalLastName: null,
      selectedContactPerson: {
        gender: null,
        title: null,
        first_name: null,
        last_name: null,
        email: null,
        department: null, //position
        phone_number: {
          prefix: null,
          number: null
        }
      },
      selectedCustomerId: null,
      selectedContactPersonId: null,
      failedRequest: false,
      validatedInputs: [
        "gender",
        "title",
        "first_name",
        "last_name",
        "email",
        "department",
        "phone_number.prefix",
        "phone_number.number"
      ]
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedCustomerId = vm.$route.params.customer_id;
      vm.selectedContactPersonId = vm.$route.params.contact_person_id;
      if (
        typeof vm.selectedCustomerId !== "undefined" &&
        vm.selectedCustomerId !== null &&
        typeof vm.selectedContactPersonId !== "undefined" &&
        vm.selectedContactPersonId !== null
      ) {
        if (vm.$route.query.contact_person_created) {
          swal("Success!", "The contact person was created.", "success").then(
            () => {
              window.history.pushState(
                {},
                document.title,
                window.location.pathname
              );
            }
          );
        }
        vm.getContactPerson(() => {
          //
        });
      }
    });
  },

  beforeRouteUpdate(to, from, next) {
    if (
      typeof this.selectedCustomerId !== "undefined" &&
      this.selectedCustomerId !== null &&
      typeof this.selectedContactPersonId !== "undefined" &&
      this.selectedContactPersonId !== null
    ) {
      this.getContactPerson(() => {
        //
      });
    }
    next();
  },

  mounted() {
    //
  },

  methods: {
    createOrUpdateContactPerson() {
      if (this.selectedContactPersonId == null) {
        this.createContactPerson();
      } else {
        this.updateContactPerson();
      }
    },

    createContactPerson() {
      if (this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint(
        "customers/" + this.selectedCustomerId + "/contact-persons"
      );

      Api()
        .post(endpoint, this.selectedContactPerson)
        .then(response => {
          this.selectedContactPersonId = response.data.data.id;
          this.$router.push({
            name: "CustomerContactDetails",
            params: {
              customer_id: this.selectedCustomerId,
              contact_person_id: this.selectedContactPersonId
            },
            query: { contact_person_created: true }
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
          this.$scrollTo("#contact-person-last-name", 500);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    getContactPerson() {
      if (this.loading) {
        return;
      }

      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint(
        "customers/" +
          this.selectedCustomerId +
          "/contact-persons/" +
          this.selectedContactPersonId
      );

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedContactPerson = response.data.data;
          this.originalFirstName = this.selectedContactPerson.first_name;
          this.originalLastName = this.selectedContactPerson.last_name;
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          console.log(error.response);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    updateContactPerson() {
      if (this.loading || this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint(
        "customers/" +
          this.selectedCustomerId +
          "/contact-persons/" +
          this.selectedContactPersonId
      );

      Api()
        .put(endpoint, this.selectedContactPerson)
        .then(() => {
          this.getContactPerson(() => {
            //
          });
          swal({
            title: "Success!",
            text: "The contact person was updated.",
            icon: "success"
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
          this.$scrollTo("#contact-person-last-name", 500);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    deleteContactPerson() {
      if (this.loading || this.updating) {
        return;
      }

      swal({
        title: "Are you sure?",
        text: "The contact person will be deleted",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "customers/" +
              this.selectedCustomerId +
              "/contact-persons/" +
              this.selectedContactPersonId
          );

          Api()
            .delete(endpoint)
            .then(() => {
              this.$router.push({
                name: "CustomerContacts",
                query: { contact_person_deleted: true }
              });
            })
            .catch(error => {
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete contact persons.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    }
  }
};
</script>

<style scoped>
.mb-4 {
  margin-bottom: 1.2rem;
}

.mt-0 {
  margin-top: 0rem;
}

.mt-8 {
  margin-top: 1.3rem;
}

.mb-0 {
  margin-bottom: 0rem;
}

.mb-1 {
  margin-bottom: 0.3rem;
}

.mb-2 {
  margin-bottom: 0.6rem;
}

.mb-8 {
  margin-bottom: 1.3rem;
}

.min-width-auto {
  width: 50px;
}
</style>
